import type { MatterTaskWithType } from "@/components/features/matter/tasks/matter-task-columns";
import MatterTasks, {
  type MatterTasksFilters,
} from "@/components/features/matter/tasks/MatterTasks";
import { Button, Card, CardContent, Typography } from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import type { MatterTaskWithRoles } from "@/hooks/matter-view-models/use-tasks-view-model";
import { useGetSubitemsForBreadcrumbs } from "@/hooks/use-get-subitems-for-breadcrumbs";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import { useInfiniteUserTasks } from "@/lib/data/react-query/matters/matter-task";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import type { Matter, MatterTask } from "@/lib/types";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";

const getUserConnectionRole = (label: string | null) => {
  const normalizedLabel = label?.toLowerCase();

  if (normalizedLabel === "assigner") {
    return "Assigner";
  }

  if (normalizedLabel?.includes("assignee")) {
    return "Assignee";
  }
};

const uniqueConnectionsByUser = <
  T extends { id: string; userId: string | null },
>(
  connections: T[],
) =>
  Array.from(
    new Map(
      connections.map((connection) => [
        connection.userId ?? connection.id,
        connection,
      ]),
    ).values(),
  );

export const DashboardTasksCard = () => {
  const navigate = useNavigate();
  const getSubItemBreadcrumbItems = useGetSubitemsForBreadcrumbs();
  const { currentUser, hasActionPermission } = useUser();
  const { openTaskModal } = useGlobalModal();
  const dashboardCreateTaskSlideoverId = "dashboard-create-task";
  const hasViewPermission = Boolean(true);
  const hasCreatePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matter,
    ACTION_PERMISSION_VALUES.update,
  );
  const [taskFilters, setTaskFilters] = useState<MatterTasksFilters>({});

  const inProgressTasksQuery = useInfiniteUserTasks({
    enabled: hasViewPermission,
    pageSize: 25,
    status: "In Progress",
    ...taskFilters,
    assigneeUserId: [currentUser.id],
  });
  const openTasksQuery = useInfiniteUserTasks({
    enabled: hasViewPermission,
    pageSize: 25,
    status: "Open",
    ...taskFilters,
    assigneeUserId: [currentUser.id],
  });
  const completedTasksQuery = useInfiniteUserTasks({
    enabled: hasViewPermission,
    pageSize: 25,
    status: "Completed",
    ...taskFilters,
    assigneeUserId: [currentUser.id],
  });
  const userTaskQueries = [
    inProgressTasksQuery,
    openTasksQuery,
    completedTasksQuery,
  ];
  const isLoading = userTaskQueries.some((query) => query.isLoading);
  const error = userTaskQueries.find(
    (query) => query.isError && query.data === undefined,
  )?.error;

  const openCreateTaskFlow = useCallback(() => {
    openTaskModal({
      matterId: "",
      editing: false,
      initialTab: "details",
      slideoverId: dashboardCreateTaskSlideoverId,
    });
  }, [dashboardCreateTaskSlideoverId, openTaskModal]);

  const allTasks = useMemo(
    () =>
      [inProgressTasksQuery, openTasksQuery, completedTasksQuery].flatMap(
        (query) => query.data?.pages.flatMap((page) => page.data) ?? [],
      ),
    [completedTasksQuery, inProgressTasksQuery, openTasksQuery],
  );

  const assigneeTasks = useMemo(() => {
    const tasksById = new Map(
      allTasks
        .filter((task) => task.label.toLowerCase().includes("assignee"))
        .map((task) => [task.taskId, task]),
    );

    return Array.from(tasksById.values()).map((task) => {
      const userConnections = task.task.userConnections ?? [];

      return {
        ...task.task,
        id: task.taskId,
        matterId: task.matterId,
        matterTitle: task.matterTitle,
        user: {
          email: task.email ?? undefined,
          firstName: task.firstName ?? "",
          id: task.userId,
          lastName: task.lastName ?? "",
          owner: task.owner ?? undefined,
          phoneNumber: task.phoneNumber ?? undefined,
        },
        assigners: uniqueConnectionsByUser(
          userConnections.filter(
            (connection) =>
              getUserConnectionRole(connection.label) === "Assigner",
          ),
        ),
        assignees: uniqueConnectionsByUser(
          userConnections.filter(
            (connection) =>
              getUserConnectionRole(connection.label) === "Assignee",
          ),
        ),
        comments: [],
        notifiers: [],
      } as MatterTask & MatterTaskWithRoles;
    });
  }, [allTasks]);

  const pendingTasks = assigneeTasks;

  const dashboardMatter = useMemo(
    () => ({
      id: `dashboard-${currentUser.id}`,
      assignees: [],
      tasks: pendingTasks,
      userConnections: pendingTasks.flatMap((task) => [
        ...task.assigners,
        ...task.assignees,
        ...task.notifiers,
      ]),
    }),
    [currentUser.id, pendingTasks],
  );

  const getCustomBreadcrumbItems = useCallback(
    (task: MatterTaskWithType) => [
      {
        id: `matter-${task.matterId}`,
        type: "Matter" as const,
        title: task.matterTitle ?? "Unknown matter",
        onNavigateClick: () => {
          void navigate({
            to: "/matters/$matterId",
            params: { matterId: task.matterId },
          });
        },
      },
      ...getSubItemBreadcrumbItems(task),
    ],
    [getSubItemBreadcrumbItems, navigate],
  );

  if (!hasViewPermission) {
    return (
      <Card className="w-full overflow-hidden">
        <CardContent className="flex h-[70vh] min-h-0 flex-col overflow-y-auto p-4">
          <Typography variant="heading">Tasks</Typography>
          <Typography variant="subHeading">
            You do not have permission to view tasks.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card className="w-full overflow-hidden">
        <CardContent className="flex h-[70vh] min-h-0 flex-col overflow-y-auto p-4">
          <div className="flex items-center justify-between">
            <Typography variant="heading">Tasks</Typography>
          </div>
          <div className="flex flex-1 items-center justify-center py-8">
            <Typography variant="subHeading">Loading tasks...</Typography>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="w-full overflow-hidden">
        <CardContent className="flex h-[70vh] min-h-0 flex-col overflow-y-auto p-4">
          <div className="flex items-center justify-between">
            <Typography variant="heading">Tasks</Typography>
          </div>
          <div className="flex flex-1 items-center justify-center py-8">
            <Typography variant="subHeading" className="text-red-500">
              Error loading tasks
            </Typography>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="w-full overflow-hidden">
        <CardContent className="flex h-[70vh] min-h-0 flex-col gap-6 p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <Typography variant="heading">Tasks</Typography>
            <div>
              {hasCreatePermission && (
                <Button type="button" onClick={openCreateTaskFlow}>
                  Create task
                </Button>
              )}
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-hidden">
            <MatterTasks
              tasks={pendingTasks}
              matter={dashboardMatter as unknown as Matter}
              defaultVariant="list"
              showAnimation={false}
              showViewToggle={false}
              showSelectTools={false}
              showCreateButton={false}
              showFilters
              showAssigneeFilter={false}
              loadMoreMode="all"
              providedTaskGroupPagination={{
                "In Progress": {
                  fetchNextPage: () => {
                    void inProgressTasksQuery.fetchNextPage();
                  },
                  hasNextPage: inProgressTasksQuery.hasNextPage,
                  isFetchingNextPage: inProgressTasksQuery.isFetchingNextPage,
                  totalCount:
                    inProgressTasksQuery.data?.pages[0]?.pagination?.count ?? 0,
                },
                Open: {
                  fetchNextPage: () => {
                    void openTasksQuery.fetchNextPage();
                  },
                  hasNextPage: openTasksQuery.hasNextPage,
                  isFetchingNextPage: openTasksQuery.isFetchingNextPage,
                  totalCount:
                    openTasksQuery.data?.pages[0]?.pagination?.count ?? 0,
                },
                Completed: {
                  fetchNextPage: () => {
                    void completedTasksQuery.fetchNextPage();
                  },
                  hasNextPage: completedTasksQuery.hasNextPage,
                  isFetchingNextPage: completedTasksQuery.isFetchingNextPage,
                  totalCount:
                    completedTasksQuery.data?.pages[0]?.pagination?.count ?? 0,
                },
              }}
              onProvidedTaskFiltersChange={setTaskFilters}
              shrinkCreateButton
              getCustomBreadcrumbItems={getCustomBreadcrumbItems}
            />
          </div>
        </CardContent>
      </Card>
    </>
  );
};
